import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Mail, RefreshCw, Copy } from "lucide-react";
import { api, copyText, rp } from "@/lib/api";

const Metric = ({ label, value, sub, testid }) => (
  <div className="bg-white border border-black/10 rounded-lg p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
    <div className="mt-1.5 text-xl font-semibold tabular-nums">{value}</div>
    {sub && <div className="mt-1 text-[11px] text-ink-soft">{sub}</div>}
  </div>
);

export default function DailySummary() {
  const [d, setD] = useState(null);
  const [recent, setRecent] = useState([]);
  const [day, setDay] = useState("");
  const [busy, setBusy] = useState("");

  const loadRecent = () => api.get("/daily-summary/recent").then((r) => setRecent(r.data)).catch(() => {});
  const load = (date) => api.get("/daily-summary", { params: { date_: date || undefined } })
    .then((r) => { setD(r.data); setDay(r.data.summary.date); })
    .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat ringkasan"));
  useEffect(() => { load(); loadRecent(); }, []);

  const act = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); toast.success(msg); loadRecent(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(""); }
  };
  const regenerate = () => act("gen", async () => {
    const r = await api.post("/daily-summary/generate", { date: day });
    setD(r.data);
  }, "Ringkasan diperbarui");
  const sendEmail = () => act("mail", () => api.post("/daily-summary/send-email", { date: day }),
    "Ringkasan dikirim ke email");

  if (!d) return <div className="text-ink-soft">Memuat ringkasan…</div>;
  const s = d.summary;

  return (
    <div className="space-y-6" data-testid="admin-daily-summary">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Ringkasan Harian</h1>
          <p className="mt-2 text-sm text-ink-soft max-w-2xl">
            Dibuat otomatis setiap {d.cron_time} dan dikirim ke {d.email_to || "email owner"}.
            Tap tombol WhatsApp untuk mengirim ringkasan ini ke nomor bisnis Anda kapan saja.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <input type="date" data-testid="summary-date" value={day} onChange={(e) => load(e.target.value)}
            className="h-10 rounded-md border border-input bg-white px-3 text-sm" />
          <button data-testid="summary-regenerate" onClick={regenerate} disabled={busy === "gen"}
            className="h-10 px-4 rounded-md border border-black/15 text-sm flex items-center gap-2">
            <RefreshCw size={14} /> {busy === "gen" ? "Menghitung…" : "Hitung ulang"}
          </button>
          <button data-testid="summary-email" onClick={sendEmail} disabled={busy === "mail" || !d.email_configured}
            className="h-10 px-4 rounded-md border border-black/15 text-sm flex items-center gap-2 disabled:opacity-50">
            <Mail size={14} /> {busy === "mail" ? "Mengirim…" : "Kirim email sekarang"}
          </button>
          <a data-testid="summary-wa" href={d.wa_link} target="_blank" rel="noreferrer"
            className="h-10 px-4 rounded-md lm-btn-primary text-sm font-semibold flex items-center gap-2">
            <MessageCircle size={14} /> Kirim ke WhatsApp
          </a>
        </div>
      </div>

      <div className="text-sm font-semibold" data-testid="summary-date-label">{s.date_label}</div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric label="Sewa hari ini" value={`${s.rentals_today} booking`} sub={`${s.units_out} unit keluar`} testid="summary-rentals" />
        <Metric label="Revenue hari ini" value={rp(s.revenue_today)} sub={`${s.paid_count} lunas · ${rp(s.paid_amount)}`} testid="summary-revenue" />
        <Metric label="Uang masuk" value={rp(s.cash_in)} sub={`refund ${rp(s.refund_out || 0)} · deposit dipegang ${rp(s.deposits_collected)}`} testid="summary-cash" />
        <Metric label="Booking baru" value={s.new_bookings} sub={`${rp(s.new_bookings_value)} · ${s.new_customers} customer baru`} testid="summary-new" />
        <Metric label="Menunggu konfirmasi" value={s.waiting_confirmation} testid="summary-waiting-confirmation" />
        <Metric label="Menunggu pembayaran" value={s.waiting_payment} testid="summary-waiting-payment" />
        <Metric label="Telat dikembalikan" value={<span className={s.overdue_returns ? "text-lm-red" : ""}>{s.overdue_returns}</span>} testid="summary-overdue" />
        <Metric label="Besok" value={`${s.tomorrow_count} booking`} sub={`${s.tomorrow_units} unit · ${rp(s.tomorrow_revenue)}`} testid="summary-tomorrow" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="summary-text-box">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">Teks siap kirim</h2>
            <button data-testid="summary-copy" onClick={async () => (await copyText(d.text)
              ? toast.success("Teks dicopy")
              : toast.error("Gagal copy — silakan pilih teksnya lalu copy manual"))}
              className="text-xs px-3 py-1.5 rounded-md border border-black/15 flex items-center gap-1.5"><Copy size={12} /> Copy</button>
          </div>
          <pre className="mt-3 text-xs whitespace-pre-wrap leading-relaxed text-ink/90 max-h-96 overflow-y-auto">{d.text}</pre>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="summary-tomorrow-list">
            <h2 className="text-sm font-semibold">Jadwal besok — {s.tomorrow_label}</h2>
            <div className="mt-2 divide-y divide-black/5 text-xs">
              {(s.tomorrow_list || []).length === 0 && <div className="py-2 text-ink-soft">Belum ada booking untuk besok.</div>}
              {(s.tomorrow_list || []).map((t) => (
                <div key={t.booking_id || t.booking_code || `${t.time}-${t.customer}`}
                  className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{t.time} · {t.customer}</div>
                    <div className="text-ink-soft truncate">{t.package} · {t.quantity} unit{t.where ? ` · ${t.where}` : ""}</div>
                  </div>
                  <span className="shrink-0 text-[10px] text-ink-soft">{t.status}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="summary-history">
            <h2 className="text-sm font-semibold">Riwayat ringkasan</h2>
            <div className="mt-2 divide-y divide-black/5 text-xs">
              {recent.length === 0 && <div className="py-2 text-ink-soft">Belum ada riwayat.</div>}
              {recent.map((r) => (
                <button key={r.date} onClick={() => load(r.date)} data-testid={`summary-history-${r.date}`}
                  className="w-full py-2 flex items-center justify-between gap-3 text-left hover:text-lm-red">
                  <span>{r.date}</span>
                  <span className="text-ink-soft">{rp(r.revenue_today)} · {r.rentals_today} sewa
                    {r.email_sent_at ? " · email ✓" : ""}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
